import React, { useEffect, useState, useCallback } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Box, Chip, useMediaQuery, useTheme } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MenuIcon from '@mui/icons-material/Menu';
import HandshakeIcon from '@mui/icons-material/Handshake';
import RefreshIcon from '@mui/icons-material/Refresh';
import Logo from "../../assets/logo.jpg";
import Profile from './Profile';
import { getPrimaryClientLogo } from '../../utils/clientLogoService';

const TopBar = ({ onMenuToggle, designVariant = 1 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const [anchorEl, setAnchorEl] = useState(null);
    const [userClient, setUserClient] = useState(null);
    const [clientLogo, setClientLogo] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [userEmail, setUserEmail] = useState('');

    const handleProfileClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Function to get current user data
    const getCurrentUserData = useCallback(() => {
        const userData = localStorage.getItem("userData");
        if (userData) {
            try {
                const parsed = JSON.parse(userData);
                console.log("[TopBar] Current user data:", parsed);
                return parsed;
            } catch (error) {
                console.error("Error parsing userData:", error);
                return null;
            }
        }
        console.log("[TopBar] No userData found in localStorage");
        return null;
    }, []);

    // Function to get user email
    const getUserEmail = useCallback(() => {
        const email = localStorage.getItem('user');
        if (email) {
            setUserEmail(email);
        }
    }, []);

    // Function to fetch client details using the new client logo service
    const fetchClientDetails = useCallback(async (clientBatch) => {
        if (!clientBatch) return;

        setIsLoading(true);
        try {
            console.log("Fetching client logo for batch:", clientBatch);
            const logoUrl = await getPrimaryClientLogo(clientBatch);
            console.log("Client logo updated:", logoUrl);
            setClientLogo(logoUrl);
        } catch (error) {
            console.error("Error fetching client logo:", error);
            setClientLogo(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Function to refresh client logo
    const refreshClientLogo = useCallback(() => {
        const currentUserData = getCurrentUserData();
        if (currentUserData?.user_batch) {
            setUserClient(currentUserData.user_batch);
            fetchClientDetails(currentUserData.user_batch);
            setRefreshKey(prev => prev + 1); // Force re-render
        }
    }, [getCurrentUserData, fetchClientDetails]);

    // Initial load and localStorage change detection
    useEffect(() => {
        const currentUserData = getCurrentUserData();
        console.log("[TopBar] User data retrieved:", currentUserData);
        console.log("[TopBar] user_batch:", currentUserData?.user_batch);
        console.log("[TopBar] programCode:", currentUserData?.programCode);
        console.log("[TopBar] batch:", currentUserData?.batch);
        
        if (currentUserData?.user_batch) {
            console.log("[TopBar] Using user_batch:", currentUserData.user_batch);
            setUserClient(currentUserData.user_batch);
            fetchClientDetails(currentUserData.user_batch);
        } else if (currentUserData?.programCode) {
            console.log("[TopBar] Using programCode:", currentUserData.programCode);
            setUserClient(currentUserData.programCode);
            fetchClientDetails(currentUserData.programCode);
        } else if (currentUserData?.batch) {
            console.log("[TopBar] Using batch:", currentUserData.batch);
            setUserClient(currentUserData.batch);
            fetchClientDetails(currentUserData.batch);
        }
        
        // Get user email
        getUserEmail();

        // Listen for localStorage changes (in case user data is updated elsewhere)
        const handleStorageChange = (e) => {
            if (e.key === 'userData') {
                refreshClientLogo();
            }
            if (e.key === 'user') {
                getUserEmail();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, [getCurrentUserData, fetchClientDetails, refreshClientLogo, getUserEmail]);

    // Re-fetch when userClient changes
    useEffect(() => {
        if (userClient) {
            fetchClientDetails(userClient);
        }
    }, [userClient, fetchClientDetails]);

    const CompanyLogoCard = () => (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0
        }}>
            <img
                src={Logo}
                alt="Company Logo"
                style={{
                    height: isMobile ? '24px' : '40px',
                    flexShrink: 0
                }}
            />
        </Box>
    );

    const ClientLogoCard = () => (
        clientLogo && (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                mr: 2
            }}>
                <img
                    src={clientLogo}
                    alt="Client Logo"
                    style={{
                        height: isMobile ? '24px' : '40px',
                        flexShrink: 0
                    }}
                />
            </Box>
        )
    );

    return (
        <AppBar position="static" color="inherit" enableColorOnDark={true} style={{ zIndex: 1100 }}>
            <Toolbar sx={{
                minHeight: isMobile ? '56px' : (designVariant === 4 ? '90px' : '64px'),
                px: isMobile ? 1 : 2
            }}>
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={onMenuToggle}
                    sx={{
                        mr: isMobile ? 1 : 2,
                        p: isMobile ? 1 : 1.5
                    }}
                >
                    <MenuIcon sx={{ fontSize: isMobile ? '20px' : '24px' }} />
                </IconButton>

                <CompanyLogoCard />

                {/* Spacer to push right side content to the right */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Client Logo before email */}
                <ClientLogoCard />

                {/* Premium Email Display - Hidden on mobile */}
                {!isMobile && userEmail && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        px: 2.5,
                        py: 1,
                        mr: 2,
                        // boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            // boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                        }
                    }}>
                        <Box sx={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: 'linear-gradient(45deg, #4CAF50, #45a049)',
                            mr: 1.5,
                            // boxShadow: '0 0 8px rgba(76, 175, 80, 0.4)',
                            animation: 'pulse 2s infinite',
                            '@keyframes pulse': {
                                '0%': { boxShadow: '0 0 8px rgba(76, 175, 80, 0.4)' },
                                '50%': { boxShadow: '0 0 16px rgba(76, 175, 80, 0.8)' },
                                '100%': { boxShadow: '0 0 8px rgba(76, 175, 80, 0.4)' }
                            }
                        }} />
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: isTablet ? '13px' : '12px',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                maxWidth: isTablet ? '180px' : '220px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                letterSpacing: '0.5px'
                            }}
                            title={userEmail}
                        >
                            {userEmail}
                        </Typography>
                    </Box>
                )}

                <IconButton
                    color="inherit"
                    title="Profile"
                    onClick={handleProfileClick}
                    sx={{
                        backgroundColor: '#0a0068',
                        p: isMobile ? 1 : 1.25,
                        borderRadius: '8px',
                        color: '#fff',
                        // boxShadow: '0 4px 16px rgba(10, 0, 104, 0.3)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: '#080056',
                            transform: 'translateY(-2px)',
                            // boxShadow: '0 8px 24px rgba(10, 0, 104, 0.4)'
                        }
                    }}
                >
                    <AccountCircleIcon sx={{ fontSize: isMobile ? '20px' : '24px' }} />
                </IconButton>

                <Profile anchorEl={anchorEl} handleClose={handleClose} />
            </Toolbar>
        </AppBar>
    );
};

export default TopBar;